import { AuthError } from './errors.ts'

/**
 * The in-flight login: the PKCE verifier and the CSRF state, alive only for the round
 * trip to the hosted UI.
 *
 * `sessionStorage`, not `localStorage`: this data is meaningless once the redirect is
 * consumed, and sessionStorage dies with the tab instead of outliving it. It is cleared
 * as soon as it is read — a verifier is single-use, and leaving it behind would let a
 * replayed callback URL be exchanged twice.
 */

const STORAGE_KEY = 'volee.auth.tx'

export interface AuthTransaction {
  readonly verifier: string
  readonly state: string
  /** Where the user meant to go, restored after the callback. */
  readonly returnTo: string
}

function storage(): Storage | null {
  // Absent in a non-DOM runtime, and throws in Safari private mode.
  try {
    return globalThis.sessionStorage ?? null
  } catch {
    return null
  }
}

export function saveTransaction(tx: AuthTransaction): void {
  storage()?.setItem(STORAGE_KEY, JSON.stringify(tx))
}

/** Reads and removes the transaction. Returns null if there is nothing pending. */
export function takeTransaction(): AuthTransaction | null {
  const store = storage()
  const raw = store?.getItem(STORAGE_KEY)
  store?.removeItem(STORAGE_KEY)
  if (!raw) return null

  const parsed: unknown = JSON.parse(raw)
  if (!isTransaction(parsed)) return null
  return parsed
}

export function clearTransaction(): void {
  storage()?.removeItem(STORAGE_KEY)
}

function isTransaction(value: unknown): value is AuthTransaction {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<AuthTransaction>
  return typeof candidate.verifier === 'string' && typeof candidate.state === 'string'
}

/**
 * Checks a callback against the pending transaction.
 *
 * Both failures are hard: a state that does not match is a CSRF attempt or a stale tab,
 * and a missing verifier means we cannot prove we started this login.
 */
export function verifyCallback(
  tx: AuthTransaction | null,
  stateFromUrl: string | null,
): AuthTransaction {
  if (!tx) {
    throw new AuthError(
      'verifier_missing',
      'No pending login: the PKCE verifier is absent, so this callback cannot be completed',
    )
  }
  if (!stateFromUrl || stateFromUrl !== tx.state) {
    throw new AuthError(
      'state_mismatch',
      'The state parameter does not match the pending login and the callback was rejected',
    )
  }
  return tx
}
